import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Lock, Building2, User as UserIcon, Phone, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getErrorMessage(d) {
  if (!d) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg);
  return null;
}

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("signup") === "1") {
      setIsRegister(true);
      setEmail(searchParams.get("email") || "");
      setOrgName(searchParams.get("org") || "");
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("session_expired") === "true") {
      toast.error("Your session has expired. Please log in again.", { duration: 5000 });
      window.history.replaceState({}, "", "/");
    }
  }, [searchParams]);

  const handleLogin = async () => {
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        toast.success("Welcome back!");
        await onLogin(response.data.user, response.data.access_token);
        return;
      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        if (status === 401) { toast.error("Invalid email or password"); break; }
        if (status === 403) { toast.error(getErrorMessage(detail) || "Your account is pending approval", { duration: 6000 }); break; }
        if (status === 422) { toast.error(getErrorMessage(detail) || "Please check your email and password"); break; }
        if (status && status >= 400 && status < 500) { toast.error(getErrorMessage(detail) || "Login failed. Please try again."); break; }
        const isRetryable = status === 503 || status === 500 || status === 502 || !error.response;
        if (isRetryable && attempt < maxRetries) { await new Promise((r) => setTimeout(r, 800)); continue; }
        if (attempt === maxRetries) toast.error("Server is temporarily unavailable. Please try again in a moment.", { duration: 6000 });
      }
    }
  };

  const handleRegister = async () => {
    try {
      await axios.post(`${API}/auth/register`, {
        org_name: orgName,
        contact_name: contactName,
        email,
        phone,
        password,
        accepted_terms: terms,
      });
      setSubmitted(true);
      toast.success("Signup request submitted!");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(getErrorMessage(detail) || "Could not submit your request. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (isRegister) await handleRegister();
    else await handleLogin();
    setLoading(false);
  };

  const switchMode = (toRegister) => {
    setIsRegister(toRegister);
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #ffffff 50%, #cffafe 100%)" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/fourpawiq-logo-gold.png" alt="FourPaw IQ" className="h-24 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Manrope" }}>FourPaw IQ</h1>
          <p className="text-base text-gray-600">Daily operations, executive insights, and lead management — all in one platform</p>
        </div>

        <Card className="shadow-2xl border-0" data-testid="login-card">
          {submitted ? (
            <CardContent className="py-10 text-center space-y-4" data-testid="signup-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Manrope" }}>Request received</h2>
              <p className="text-base text-gray-600">
                Thanks, {contactName || "there"}! Your signup for <span className="font-semibold">{orgName}</span> is
                pending review. We'll email <span className="font-semibold">{email}</span> once it's approved.
              </p>
              <Button onClick={() => switchMode(false)} className="mt-2" data-testid="back-to-login-btn" variant="outline">
                Back to sign in
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-bold" style={{ fontFamily: "Manrope" }}>
                  {isRegister ? "Request Access" : "Welcome Back"}
                </CardTitle>
                <CardDescription className="text-base">
                  {isRegister ? "Tell us about your business — we'll review and get you set up" : "Login to access your dashboard"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isRegister && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="orgName" className="text-sm font-medium">Business / Organization name</Label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input id="orgName" type="text" placeholder="Acme Pet Resort" value={orgName}
                            onChange={(e) => setOrgName(e.target.value)} required className="pl-10 h-11" data-testid="org-name-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactName" className="text-sm font-medium">Your name</Label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input id="contactName" type="text" placeholder="Jane Doe" value={contactName}
                            onChange={(e) => setContactName(e.target.value)} required className="pl-10 h-11" data-testid="contact-name-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input id="phone" type="tel" placeholder="(555) 123-4567" value={phone}
                            onChange={(e) => setPhone(e.target.value)} className="pl-10 h-11" data-testid="phone-input" />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input id="email" type="email" placeholder="name@example.com" value={email}
                        onChange={(e) => setEmail(e.target.value)} required className="pl-10 h-11" data-testid="email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                      {!isRegister && (
                        <button type="button" onClick={() => navigate("/forgot-password")}
                          className="text-xs font-medium text-cyan-600 hover:text-cyan-700 hover:underline">
                          Forgot Password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input id="password" type="password" placeholder="••••••••" value={password}
                        onChange={(e) => setPassword(e.target.value)} required minLength={isRegister ? 8 : undefined}
                        className="pl-10 h-11" data-testid="password-input" />
                    </div>
                    {isRegister && <p className="text-xs text-gray-400">At least 8 characters. You'll use this to log in once approved.</p>}
                  </div>
                  {isRegister && (
                    <label className="flex items-start gap-2 text-sm text-gray-600" data-testid="signup-terms">
                      <input type="checkbox" className="mt-1" checked={terms} onChange={(e) => setTerms(e.target.checked)} required data-testid="signup-terms-checkbox" />
                      <span>I agree to the FourPawIQ Terms of Service, Privacy Policy and data agreement.</span>
                    </label>
                  )}
                  <Button type="submit"
                    className="w-full h-11 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                    disabled={loading} data-testid="submit-button">
                    {loading ? "Processing..." : isRegister ? "Submit Request" : "Sign In"}
                  </Button>
                </form>
              </CardContent>
              <CardFooter className="flex flex-col space-y-2">
                <div className="text-sm text-center text-gray-600">
                  {isRegister ? "Already have an account?" : "Don't have an account?"}
                  <button onClick={() => switchMode(!isRegister)}
                    className="ml-2 font-semibold text-cyan-600 hover:text-cyan-700" data-testid="toggle-auth-mode">
                    {isRegister ? "Sign In" : "Request Access"}
                  </button>
                </div>
              </CardFooter>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Login;
